import type { TenantRoleCode } from "@planerius/contracts/cabinet-auth"
import type { SystemRoleDefinition } from "@planerius/contracts/cabinet-roles"
import { SYSTEM_ROLES } from "@planerius/contracts/cabinet-roles"

/**
 * Текст отказа в разделе (1-Э1.11).
 *
 * КОДА ПРАВА В ТЕКСТЕ БОЛЬШЕ НЕТ. «Попросите открыть право reports.view» — это просьба,
 * которую человек не может произнести: владелец такого слова не знает, в интерфейсе
 * ролей его тоже нет. Вместо кода — роль спрашивающего и роли, у которых доступ есть:
 * с этим он идёт к конкретному человеку и говорит понятное.
 *
 * СПИСОК РОЛЕЙ СЧИТАЕТСЯ ИЗ ТОГО ЖЕ ИСТОЧНИКА, ЧТО И САМИ ПРАВА (SYSTEM_ROLES): иначе
 * текст разойдётся с матрицей на первой же правке, и человек пойдёт просить не у того.
 * Свои роли салона сюда не попадают — мы про них ничего не знаем, поэтому говорим
 * «владелец салона» как последнюю верную инстанцию.
 */
export interface DeniedText {
  readonly title: string
  readonly text: string
}

/** Род ролей для фразы «у владельца и управляющего». */
const ROLE_GENITIVE: Readonly<Record<TenantRoleCode, string>> = {
  owner: "владельца",
  manager: "управляющего",
  admin: "администратора",
  master: "мастера",
  accountant: "бухгалтера",
  marketer: "маркетолога",
  viewer: "наблюдателя",
}

const ROLE_NOMINATIVE: Readonly<Record<TenantRoleCode, string>> = {
  owner: "владелец",
  manager: "управляющий",
  admin: "администратор",
  master: "мастер",
  accountant: "бухгалтер",
  marketer: "маркетолог",
  viewer: "наблюдатель",
}

export function rolesWithPermission(permission: string): readonly TenantRoleCode[] {
  return SYSTEM_ROLES.filter((role: SystemRoleDefinition) =>
    (role.permissions as readonly string[]).includes(permission),
  ).map((role: SystemRoleDefinition) => role.code)
}

export function deniedText(input: {
  readonly sectionLabel: string
  readonly permission: string
  /** Код системной роли зрителя. null — у салона своя роль, названия мы не знаем. */
  readonly viewerRole: TenantRoleCode | null
  readonly viewerRoleName: string
}): DeniedText {
  const holders = rolesWithPermission(input.permission).filter((code) => code !== input.viewerRole)
  const who =
    holders.length === 0
      ? "владельца салона"
      : holders.map((code) => ROLE_GENITIVE[code]).join(" и ")

  const mine =
    input.viewerRole === null
      ? `Ваша роль — «${input.viewerRoleName}».`
      : `Ваша роль — ${ROLE_NOMINATIVE[input.viewerRole]}.`

  return {
    title: "Этот раздел вам недоступен",
    text: `«${input.sectionLabel}» открыт у ${who}. ${mine} Попросите владельца салона открыть вам этот раздел — остальные продолжают работать.`,
  }
}
